// Goods in, one PO line: what a person has to do next, in as few words as the data allows.
//
// PRESENTATION ONLY. Nothing here judges a certificate, covers a batch or decides anything: every
// verdict, match and proof is the server's (api/src/domain/cofaRules.ts), and this only counts and
// groups what it said. Two rules hold throughout:
//  - Rule 15: a list that did not arrive is "not known" (null), never an empty list.
//  - A FAIL or an OUT is never folded away: it is always a need of its own.
//
// Coverage across certificates: a supplier often sends one batch's analysis, micro and lab
// results as separate files, and each is judged alone, so each lists the others' checks as "not
// reported". A required check counts as reported here when ANY live certificate naming the batch
// reports it, paired to that same spec check by the server (specParameterId, same spec version).
// A result the server did not pair is never counted; it is offered as a hint for a person.
import { DECISION_LABEL, dayLabel, type AttestationKind, type Certificate, type ReceiptDetail, type RegisterRow, type Tone } from './goodsInModel';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The kinds already attested on a certificate's lineage: offering one again is offering a refusal. */
export function attestedKinds(certificates: readonly Certificate[], cofaId: number): Set<AttestationKind> {
  const byId = new Map(certificates.map((c) => [c.cofaId, c]));
  const out = new Set<AttestationKind>();
  const seen = new Set<number>();
  for (let c = byId.get(cofaId); c && !seen.has(c.cofaId); c = c.supersedes ? byId.get(c.supersedes) : undefined) {
    seen.add(c.cofaId);
    for (const a of c.attestations ?? []) out.add(a.kind);
  }
  return out;
}

type Batch = Pick<ReceiptDetail['batches'][number], 'batchId' | 'packingSlipId'>;

const live = (certificates: readonly Certificate[]) => certificates.filter((c) => c.supersededBy === null);
const entryFor = (c: Certificate, batch: Batch) =>
  (c.coverage?.batches ?? []).find((x) => x.batchId === batch.batchId && (x.packingSlipId ?? '') === (batch.packingSlipId ?? ''));

/** The live certificates whose lot IS this batch, as the server matched it (EXACT only - rule 18:
 *  a NEAR lot names a cause and is never a match, so it never counts toward the batch).
 *  With no batch (nothing receipted, or no batch in D365), every live certificate on the line. */
export function certificatesFor(certificates: readonly Certificate[], batch: Batch | null): Certificate[] {
  if (!batch) return live(certificates);
  return live(certificates).filter((c) => entryFor(c, batch)?.match === 'EXACT');
}

export interface StillMissing {
  parameter: string;
  /** Results the server did NOT pair to any spec check whose name shares a word with this one.
   *  A hint for a person, never counted as reported. */
  perhaps: Array<{ parameter: string; cofaId: number }>;
}

const STOP = new Set(['total', 'count', 'value', 'type', 'with', 'kernels', 'damage', 'per']);
function words(name: string): Set<string> {
  return new Set(
    name
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .map((w) => (w.length > 4 && w.endsWith('s') ? w.slice(0, -1) : w))
      .filter((w) => w.length >= 4 && !STOP.has(w)),
  );
}

/** Some certificate in the group was judged against a spec version: without one there is no list of
 *  required checks, and an empty "missing" list says nothing. */
export const judgedAgainstSpec = (group: readonly Certificate[]) => group.some((c) => c.specVersionId != null);

/** Required checks no certificate in the group reports. Null when it cannot be told: a
 *  certificate's list of missing checks did not arrive, or no certificate was judged against a spec
 *  (rule 15 - judgedAgainstSpec says which). Empty = every one reported. */
export function missingAcross(group: readonly Certificate[]): StillMissing[] | null {
  if (group.length === 0) return null;
  if (!judgedAgainstSpec(group)) return null;
  if (group.some((c) => c.missing === undefined)) return null;
  const spec = (c: Certificate) => String(c.specVersionId ?? 'none');
  const reported = new Set<string>();
  for (const c of group) for (const r of c.results) if (r.specParameterId != null) reported.add(`${spec(c)}:${r.specParameterId}`);
  const unpaired = group.flatMap((c) =>
    c.results.filter((r) => r.specParameterId == null).map((r) => ({ parameter: r.parameter, cofaId: c.cofaId, words: words(r.parameter) })),
  );
  const seen = new Set<string>();
  const out: StillMissing[] = [];
  for (const c of group) {
    for (const m of c.missing ?? []) {
      const key = m.specParameterId != null ? `${spec(c)}:${m.specParameterId}` : `${spec(c)}:name:${m.parameter.toLowerCase()}`;
      if (reported.has(key) || seen.has(key)) continue;
      seen.add(key);
      const w = words(m.parameter);
      out.push({
        parameter: m.parameter,
        perhaps: unpaired.filter((u) => [...u.words].some((x) => w.has(x))).map(({ parameter, cofaId }) => ({ parameter, cofaId })),
      });
    }
  }
  return out;
}

/* ── The decision view (Amit, 24 Oct 2026: the decision is the one QA step) ───────── */

type DecideBatch = Batch & { approved: boolean | null; onPaper?: boolean | null };

/** The server's word that an undecided certificate asks for no decision (registerState). */
export type NoDecision = NonNullable<RegisterRow['noDecisionNeeded']>[number];

/** What Details says of a certificate that needs no decision of its own. */
export const noDecisionText = (n: NoDecision) =>
  `No decision needed (batch approved on ${dayLabel(n.approvedAt, true)} with #${n.approvedCofaId} in view)`;

/** Newest first, as the server orders live certificates (registerState: by recordedAt). */
const newestFirst = (a: Certificate, b: Certificate) => (b.recordedAt ?? '').localeCompare(a.recordedAt ?? '') || b.cofaId - a.cofaId;

export interface DecisionGroup<B extends DecideBatch = DecideBatch> {
  key: string;
  /** The batches this group's decision is about; empty for a line with none (or none known). */
  batches: B[];
  /** The newest live certificate naming them: the one the decision form is for. */
  target: Certificate | null;
  /** Every live certificate naming them (exact, else near), newest first. */
  certificates: Certificate[];
  /** Live certificates whose lot is close to or exactly no batch received. */
  matchesNoBatch: boolean;
  /** Of the line's certificates, those the server says need no decision, by id. Never asked for. */
  noDecision: ReadonlyMap<number, NoDecision>;
}

/** One decision per batch, on the newest live certificate naming it EXACTLY (else the newest naming a
 *  lot close to it - approving that one takes a note, which the server says why). Batches sharing that
 *  certificate share the decision, so they share a group. Rule 15: when the batches could not be read,
 *  or there are none, nothing is said per batch - one group for the line. PRESENTATION ONLY: which
 *  batches a decision covers is the server's answer (rule 18), shown as each batch's `approved`. */
export function decisionGroups<B extends DecideBatch>(
  certificates: readonly Certificate[],
  batches: readonly B[],
  batchReadError: string | null,
  noDecisionNeeded: readonly NoDecision[] = [],
): DecisionGroup<B>[] {
  const all = live(certificates).sort(newestFirst);
  // Rule 15: under a read error the server claims none, and neither does this.
  const noDecision = new Map(batchReadError ? [] : noDecisionNeeded.map((n) => [n.cofaId, n]));
  if (batchReadError || batches.length === 0) {
    return [{ key: 'line', batches: [], target: all[0] ?? null, certificates: all, matchesNoBatch: false, noDecision }];
  }
  const groups = new Map<string, DecisionGroup<B>>();
  for (const b of batches) {
    const exact = certificatesFor(certificates, b);
    const pool = (exact.length ? exact : all.filter((c) => entryFor(c, b)?.match === 'NEAR')).sort(newestFirst);
    // The decision is asked of a certificate that still needs one; one the server settled is not it.
    const target = pool.find((c) => !noDecision.has(c.cofaId)) ?? pool[0] ?? null;
    const key = target ? `cofa-${target.cofaId}` : `batch-${b.batchId}-${b.packingSlipId ?? ''}`;
    const g = groups.get(key) ?? { key, batches: [], target, certificates: [], matchesNoBatch: false, noDecision };
    g.batches.push(b);
    for (const c of pool) if (!g.certificates.some((x) => x.cofaId === c.cofaId)) g.certificates.push(c);
    groups.set(key, g);
  }
  const placed = new Set<number>();
  for (const g of groups.values()) {
    g.certificates.sort(newestFirst);
    for (const c of g.certificates) placed.add(c.cofaId);
  }
  const rest = all.filter((c) => !placed.has(c.cofaId));
  const out = [...groups.values()];
  if (rest.length) out.push({ key: 'none', batches: [], target: rest[0], certificates: rest, matchesNoBatch: true, noDecision });
  return out;
}

export interface OutOfSpec {
  results: Array<{ cofaId: number; parameter: string; result: string; limit: string | null }>;
  /** Certificates that FAILED with no result marked OUT: never folded away either. */
  failed: number[];
}

/** Every OUT result and every FAIL across the certificates: always shown, never behind Details. */
export function outOfSpec(group: readonly Certificate[]): OutOfSpec {
  const results = group.flatMap((c) =>
    c.results
      .filter((r) => r.verdict === 'OUT')
      .map((r) => ({
        cofaId: c.cofaId,
        parameter: r.parameter,
        result: `${r.result}${r.unit && !r.result.includes(r.unit) ? ` ${r.unit}` : ''}`,
        limit: r.printedLimit,
      })),
  );
  const failed = group.filter((c) => c.verdict === 'FAIL' && !c.results.some((r) => r.verdict === 'OUT')).map((c) => c.cofaId);
  return { results, failed };
}

/** The Next line for one batch of a group: "F51725: decide", or who decided what and when, plus a count
 *  of what is out of spec. A batch the server could not answer for is "not known" (rule 15). */
export function batchNext(b: DecideBatch | null, g: DecisionGroup): { text: string; tone: Tone } {
  const { label, rest, tone } = nextParts(b, g);
  return { text: `${label}: ${rest}`, tone };
}

/** The newest APPROVED decision standing on any of the certificates, and which one carries it. */
function standingApproval(certificates: readonly Certificate[]) {
  let best: { cofaId: number; upn: string; at: string | null } | null = null;
  for (const c of certificates) {
    const l = c.decisions[c.decisions.length - 1];
    if (l?.decision !== 'APPROVED') continue;
    if (!best || (l.decidedAt ?? '') > (best.at ?? '')) best = { cofaId: c.cofaId, upn: l.decidedByUpn, at: l.decidedAt };
  }
  return best;
}

function nextParts(b: DecideBatch | null, g: DecisionGroup): { label: string; rest: string; tone: Tone } {
  const label = b?.batchId ?? (g.matchesNoBatch ? 'Matching no batch received' : 'This line');
  if (b?.onPaper) return { label, rest: 'on paper', tone: 'quiet' };
  const t = g.target;
  if (!t) return { label, rest: 'no certificate names it yet', tone: 'warn' };
  if (b && b.approved === null) return { label, rest: 'not known', tone: 'quiet' };
  const latest = t.decisions[t.decisions.length - 1] ?? null;
  const decided = latest !== null && (latest.decision !== 'APPROVED' || b === null || b.approved === true);
  // UI/UX audit, 31 Oct 2026, UX-12: a batch the server says IS approved (rule 18's coverage) whose
  // newest certificate is still undecided used to read "decide", beside a card saying "All approved".
  // The batch's approval is the server's answer and is said as such; the undecided certificate is
  // still named (line state, warn), never folded away and never read as the batch being unapproved.
  const approvedNotTarget = !decided && latest === null && b !== null && b.approved === true;
  const by = approvedNotTarget ? standingApproval(g.certificates.filter((c) => c !== t)) : null;
  let rest = decided
    ? `decided: ${DECISION_LABEL[latest.decision]} by ${latest.decidedByUpn} on ${dayLabel(latest.decidedAt, true)}`
    : approvedNotTarget
      ? `${by ? `approved on #${by.cofaId} by ${by.upn}` : 'approved'} · #${t.cofaId} not decided yet`
      : latest
        ? `decide (the approval on #${t.cofaId} does not cover it)`
        : 'decide';
  const out = outOfSpec(g.certificates);
  if (out.results.length) rest += ` · ${plural(out.results.length, 'result')} out of spec`;
  if (out.failed.length) rest += ` · ${out.failed.map((id) => `#${id}`).join(', ')} failed`;
  // A certificate the server says needs no decision is not asked about: Details says why.
  const others = g.certificates.filter((c) => c !== t && c.decisions.length === 0 && !g.noDecision.has(c.cofaId));
  if (others.length) rest += ` · also not decided: ${others.map((c) => `#${c.cofaId}`).join(', ')}`;
  const bad = out.results.length > 0 || out.failed.length > 0 || (decided && latest.decision !== 'APPROVED');
  return { label, rest, tone: bad ? 'bad' : decided ? 'ok' : 'warn' };
}

/** The Next list, one line per batch, with batches whose line says the same thing in the same tone
 *  folded into one: "40 batches, 605712015 … 613611018: approved on #80 by anna@ · #86 not decided
 *  yet" (UI/UX audit, 31 Oct 2026, UX-12: forty identical lines buried the one that differed). Only
 *  identical words fold, so no batch is ever said to be in a state another batch is in. */
export function nextLines(groups: readonly DecisionGroup[]): Array<{ key: string; text: string; tone: Tone }> {
  const rows: Array<{ key: string; labels: string[]; rest: string; tone: Tone }> = [];
  const byWords = new Map<string, (typeof rows)[number]>();
  for (const g of groups) {
    for (const b of g.batches.length ? g.batches : [null]) {
      const { label, rest, tone } = nextParts(b, g);
      const key = `${g.key}-${b ? `${b.batchId}-${b.packingSlipId ?? ''}` : ''}`;
      const same = b ? byWords.get(`${tone}|${rest}`) : undefined;
      if (same) {
        same.labels.push(label);
        continue;
      }
      const row = { key, labels: [label], rest, tone };
      rows.push(row);
      if (b) byWords.set(`${tone}|${rest}`, row);
    }
  }
  return rows.map(({ key, labels, rest, tone }) => {
    if (labels.length === 1) return { key, text: `${labels[0]}: ${rest}`, tone };
    const named = labels.length <= 3 ? labels.join(', ') : `${labels[0]} … ${labels[labels.length - 1]}`;
    return { key, text: `${labels.length} batches, ${named}: ${rest}`, tone };
  });
}

/** A batch card's heading. One batch is named; up to three are listed; more are the first and the last
 *  and how many, the way Next folds them (UI/UX audit, 31 Oct 2026, UX-25: one heading read "Batch
 *  605712015, 605712016, … 613611018", forty ids in an h2). Every batch is still in Batches in D365,
 *  and Next says each batch's standing. */
export function batchGroupTitle(batches: readonly Pick<Batch, 'batchId'>[]): string {
  const ids = batches.map((b) => b.batchId);
  if (ids.length === 1) return `Batch ${ids[0]}`;
  if (ids.length <= 3) return `Batches ${ids.join(', ')}`;
  return `Batches ${ids[0]} … ${ids[ids.length - 1]} (${ids.length})`;
}

/** How old the line's D365 snapshot may be before the page says so: the auto-approve sweep's own
 *  `snapshot_stale` threshold (rule 25, audit 28 Oct 2026), so the screen and the job agree on "old". */
export const SNAPSHOT_STALE_MS = 24 * 60 * 60 * 1000;

/** Whether the line's snapshot is older than a day (UI/UX audit, 31 Oct 2026, UX-28: "SNAPSHOT Oct 23"
 *  was eight days old and read as a plain fact beside "Approved: No"). Rule 15: no time, or one that
 *  does not parse, is 'unknown' - never 'fresh'. */
export function snapshotAge(syncedAt: string | null | undefined, now: number = Date.now()): 'fresh' | 'stale' | 'unknown' {
  if (!syncedAt) return 'unknown';
  const t = new Date(syncedAt).getTime();
  if (Number.isNaN(t)) return 'unknown';
  return now - t > SNAPSHOT_STALE_MS ? 'stale' : 'fresh';
}
